import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { RequirementItem } from 'components/DynamicFormFields';
import { t } from 'i18next';
import { FormProvider, Form } from 'react-hook-form';
import { useEditAssessmentModal } from './hooks';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import { paths } from '@common/types';
import { CloudDone } from '@mui/icons-material';
import { theme } from '@store';
import { ConfirmationModal, CustomModal, StatusBanner } from '@components';
import { CopyAssessmentLink } from './components';

interface IEditAssessmentModalProps {
  patient_id: string;
  handleClose: () => void;
  assessment: paths[`/patient/{patient_id}/assessment/{assessment_id}`]['get']['responses']['200']['content']['application/json'];
}

export const EditAssessmentModal = ({
  patient_id,
  handleClose,
  assessment,
}: IEditAssessmentModalProps) => {
  const {
    formMethods,
    isSavingAssessment,
    handleChangeField,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteAssessment,
    isDeletingAssessment,
  } = useEditAssessmentModal({
    patient_id,
    assessment_id: assessment.id,
    assessment,
    onDeleted: handleClose,
  });

  const { control } = formMethods;

  return (
    <CustomModal
      isOpen
      onClose={handleClose}
      isLoading={isSavingAssessment}
      title=''
      hideHeader
      disableCloseOnOutsideClick={isSavingAssessment}
      size='xl'
    >
      <FormProvider {...formMethods}>
        <Form
          control={control}
          // onSubmit={e => {
          //   return onSubmit(e.event);
          // }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              pt: 3,
              pb: 2,
              mb: 1,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Typography variant='h6' component='h2'>
              {assessment?.name || 'Edit assessment'}
            </Typography>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {assessment?.completed ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: '1.5rem',
                    color: theme.palette.success.main,
                  }}
                />
              ) : (
                <WarningIcon
                  sx={{ fontSize: '1.5rem', color: theme.palette.red.main }}
                />
              )}

              <Typography
                variant='subtitle1'
                sx={{
                  color: assessment?.completed
                    ? theme.palette.success.main
                    : theme.palette.red.main,
                }}
              >
                {assessment?.completed ? 'Completed' : 'Incomplete'}
              </Typography>
              <IconButton
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 0,
                  display: 'flex',
                  ':hover': {
                    cursor: 'pointer',
                    bgcolor: theme => theme.palette.grey[50],
                    borderRadius: '6px',
                  },
                }}
                onClick={handleClose}
                disabled={isSavingAssessment}
              >
                <ClearIcon />
              </IconButton>
            </Box>
          </Box>

          {assessment.warning_text && (
            <StatusBanner variant='warning' text={assessment.warning_text} />
          )}

          <CopyAssessmentLink patient_id={patient_id} assessment={assessment} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: 'column',
              width: '100%',
              gap: '2rem',
            }}
          >
            {assessment?.requirements.map((req, index) => (
              <RequirementItem
                key={`${req.title} ${index}`}
                {...req}
                patient_id={patient_id}
                handleChangeField={handleChangeField}
              />
            ))}
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              position: 'sticky',
              bottom: 0,
              paddingY: 3,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Tooltip
              title={
                assessment.cannot_be_deleted_reason
                  ? t(
                      `patient.assessments.cannotDelete.${assessment.cannot_be_deleted_reason}`,
                    )
                  : ''
              }
              disableHoverListener={assessment.can_be_deleted}
              disableFocusListener={assessment.can_be_deleted}
              disableTouchListener={assessment.can_be_deleted}
              placement='top'
            >
              <Box
                sx={{
                  cursor: !assessment.can_be_deleted ? 'not-allowed' : 'unset',
                }}
              >
                <Button
                  size='large'
                  color='error'
                  variant='text'
                  startIcon={<DeleteIcon />}
                  onClick={handleOpenDeleteModal}
                  disabled={
                    !assessment.can_be_deleted ||
                    isSavingAssessment ||
                    isDeletingAssessment
                  }
                >
                  {t('general.delete')}
                </Button>
              </Box>
            </Tooltip>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {isSavingAssessment ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <CircularProgress
                    size='24px'
                    sx={{ color: 'rgba(0, 0, 0, 0.12)' }}
                  />
                  <Typography sx={{ color: 'text.disabled' }}>
                    {t('general.isSaving')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <CloudDone
                    sx={{ height: '24px', width: '32px' }}
                    color='disabled'
                  />
                  <Typography sx={{ color: 'text.disabled' }}>
                    {t('general.saved')}
                  </Typography>
                </Box>
              )}
              <Button
                size='large'
                disabled={isSavingAssessment}
                onClick={handleClose}
                color='secondary'
                variant='contained'
                sx={{ color: theme.palette.common.white }}
              >
                {t('general.confirm')}
              </Button>
            </Box>
          </Box>
        </Form>
      </FormProvider>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteAssessment}
        isLoading={isDeletingAssessment}
        title={t('patient.assessments.deleteModal.title')}
        message={t('patient.assessments.deleteModal.message')}
        confirmText={t('general.delete')}
      />
    </CustomModal>
  );
};
